import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  MapPin, Search, Building2, Trees, Globe2, ChevronLeft, ChevronRight, ExternalLink, ChevronRight as Crumb,
} from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { resolveCountry, FEATURED_COUNTRIES, COUNTRY_LIST } from "../lib/countries";

// Fix default marker icons (CRA / webpack break Leaflet's icon paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const SPORTS = [
  { slug: "", name: "All sports" },
  { slug: "padel", name: "Padel" },
  { slug: "pickleball", name: "Pickleball" },
  { slug: "tennis", name: "Tennis" },
  { slug: "golf", name: "Golf" },
  { slug: "basketball", name: "Basketball" },
  { slug: "football", name: "Football" },
  { slug: "volleyball", name: "Volleyball" },
];

const ACCESS_OPTIONS = [
  { value: "", label: "Any access" },
  { value: "public", label: "Public" },
  { value: "semi_public", label: "Semi-public" },
  { value: "private", label: "Private / members" },
];

const PAGE_SIZE = 30;

export default function FacilitiesDirectory() {
  // SEO-friendly URL params: /facilities/sport/:sport/in/:country
  const { sport: urlSport, country: urlCountry } = useParams();
  const resolvedCountry = useMemo(() => resolveCountry(urlCountry), [urlCountry]);
  const sportFromUrl = useMemo(() => {
    if (!urlSport) return "";
    const found = SPORTS.find((s) => s.slug && s.slug.toLowerCase() === urlSport.toLowerCase());
    return found ? found.slug : "";
  }, [urlSport]);

  const [filters, setFilters] = useState({
    sport: sportFromUrl,
    country: resolvedCountry?.iso || "",
    q: "",
    access_type: "",
    indoor: "",
  });

  // Re-sync if URL changes (back/forward navigation between landing pages)
  useEffect(() => {
    setFilters((f) => ({ ...f, sport: sportFromUrl, country: resolvedCountry?.iso || "" }));
  }, [sportFromUrl, resolvedCountry]);
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ results: [], total: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip), include_osm: "false" });
      if (filters.sport) params.set("sport", filters.sport);
      if (filters.country) params.set("country", filters.country);
      if (filters.q) params.set("q", filters.q);
      if (filters.access_type) params.set("access_type", filters.access_type);
      if (filters.indoor) params.set("indoor", filters.indoor);
      const { data: res } = await api.get(`/facilities?${params.toString()}`);
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load directory");
    } finally { setLoading(false); }
  }, [filters, skip]);

  useEffect(() => { load(); }, [load]);

  // Reset pagination on filter change
  useEffect(() => { setSkip(0); }, [filters]);

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  // Map view: centered on first result if any, otherwise world view
  const mapCenter = useMemo(() => {
    const first = data.results?.[0];
    if (first?.lat && first?.lng) return [first.lat, first.lng];
    return [25, 0]; // default world-ish view
  }, [data.results]);
  const mapZoom = data.results?.[0]?.lat ? 5 : 2;
  const markers = useMemo(() => (data.results || []).filter((f) => f.lat && f.lng), [data.results]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  // ============== SEO: dynamic title + meta description ==============
  const sportName = useMemo(() => {
    if (!filters.sport) return null;
    const s = SPORTS.find((x) => x.slug === filters.sport);
    return s ? s.name : null;
  }, [filters.sport]);
  const countryName = resolvedCountry?.name || null;
  const accessLabel = useMemo(() => {
    const a = ACCESS_OPTIONS.find((x) => x.value === filters.access_type);
    return a && a.value ? a.label.toLowerCase() : null;
  }, [filters.access_type]);

  const heading = useMemo(() => {
    const parts = [];
    if (accessLabel) parts.push(accessLabel.charAt(0).toUpperCase() + accessLabel.slice(1));
    if (sportName) parts.push(sportName.toLowerCase());
    parts.push(parts.length === 0 ? "Sport clubs and facilities" : sportName ? "clubs" : "");
    if (countryName) parts.push(`in ${countryName}`);
    return parts.filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  }, [sportName, countryName, accessLabel]);

  const subtitle = useMemo(() => {
    const total = (data.total ?? 0).toLocaleString();
    if (sportName && countryName) return `${total} ${sportName.toLowerCase()} ${sportName.toLowerCase().endsWith("ll") ? "facilities" : "clubs"} in ${countryName}`;
    if (sportName) return `${total} ${sportName.toLowerCase()} venues worldwide`;
    if (countryName) return `${total} sport clubs and golf courses in ${countryName}`;
    return `${total} sport clubs and golf courses worldwide`;
  }, [sportName, countryName, data.total]);

  useEffect(() => {
    const base = heading || "Facilities directory";
    document.title = `${base} | Pick2Play`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = `${subtitle}. Browse sport clubs, padel courts, tennis centres, and golf courses${countryName ? ` in ${countryName}` : " worldwide"} on Pick2Play.`;
  }, [heading, subtitle, countryName]);
  // ====================================================================

  return (
    <AppLayout>
      <div data-testid="facilities-directory">
        {/* Breadcrumbs */}
        {(sportName || countryName) && (
          <nav className="text-xs text-zinc-500 mb-3 flex items-center gap-1 flex-wrap" data-testid="dir-breadcrumbs">
            <Link to="/facilities" className="hover:text-[#00E676]">All facilities</Link>
            {sportName && (
              <>
                <Crumb size={12} />
                <Link to={`/facilities/sport/${urlSport || filters.sport}`} className="hover:text-[#00E676]">{sportName}</Link>
              </>
            )}
            {countryName && (
              <>
                <Crumb size={12} />
                <span className="text-zinc-300">{countryName}</span>
              </>
            )}
          </nav>
        )}

        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black flex items-center gap-3 capitalize">
              <Globe2 className="text-[#00E676]" size={40} /> {heading}
            </h1>
            <p className="text-zinc-400 mt-2">
              {subtitle}. Filter by sport, country, indoor/outdoor, and access type.
            </p>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mt-6 pp-card p-3 flex flex-wrap items-center gap-2" data-testid="dir-filters">
          <select value={filters.sport} onChange={(e) => setF("sport")(e.target.value)} className="pp-input" data-testid="dir-filter-sport">
            {SPORTS.map((s) => <option key={s.slug || "all"} value={s.slug}>{s.name}</option>)}
          </select>
          <input
            value={filters.country}
            onChange={(e) => setF("country")(e.target.value.toUpperCase())}
            placeholder="Country (ES, US…)"
            maxLength={3}
            className="pp-input w-32"
            data-testid="dir-filter-country"
          />
          <select value={filters.access_type} onChange={(e) => setF("access_type")(e.target.value)} className="pp-input" data-testid="dir-filter-access">
            {ACCESS_OPTIONS.map((a) => <option key={a.value || "any"} value={a.value}>{a.label}</option>)}
          </select>
          <select value={filters.indoor} onChange={(e) => setF("indoor")(e.target.value)} className="pp-input" data-testid="dir-filter-indoor">
            <option value="">Any setting</option>
            <option value="true">Indoor only</option>
            <option value="false">Outdoor only</option>
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={filters.q}
              onChange={(e) => setF("q")(e.target.value)}
              placeholder="Search by venue name…"
              className="pp-input ps-9 w-full"
              data-testid="dir-filter-q"
            />
          </div>
          <button
            onClick={() => { setFilters({ sport: "", country: "", q: "", access_type: "", indoor: "" }); }}
            className="pp-btn-secondary px-3 py-2 rounded-lg text-xs"
            data-testid="dir-filter-clear"
          >Clear</button>
        </div>

        {/* Two-column layout: map + list */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Map */}
          <div className="pp-card overflow-hidden h-[480px] sticky top-24" data-testid="dir-map">
            <MapContainer center={mapCenter} zoom={mapZoom} key={`${mapCenter.join(",")}-${mapZoom}`}
              scrollWheelZoom={false} className="h-full w-full">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {markers.map((f) => (
                <Marker key={f.id} position={[f.lat, f.lng]}>
                  <Popup>
                    <div className="text-sm" style={{ color: "#111" }}>
                      <Link to={`/facilities/${f.slug || f.id}`} className="font-bold hover:underline">{f.name}</Link>
                      <div className="text-xs text-zinc-600 mt-1">{[f.city, f.country].filter(Boolean).join(", ")}</div>
                      <div className="text-[11px] mt-1">{(f.sport_slugs || []).join(", ")}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* List */}
          <div data-testid="dir-list">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => <div key={i} className="pp-card p-4 h-20 animate-pulse" />)}
              </div>
            ) : data.results.length === 0 ? (
              <div className="pp-card p-10 text-center text-zinc-500" data-testid="dir-empty">
                <MapPin size={32} className="mx-auto mb-3 text-zinc-700" />
                <div className="font-bold text-lg mb-1">No matches</div>
                <div className="text-sm">Try removing filters or searching for a different city / venue name.</div>
              </div>
            ) : (
              <div className="space-y-2">
                {data.results.map((f) => <DirectoryRow key={f.id} f={f} />)}
              </div>
            )}

            {/* Pagination */}
            {data.total > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between" data-testid="dir-pagination">
                <button
                  onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                  disabled={skip === 0}
                  className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-30"
                  data-testid="dir-prev"
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                <span className="text-xs text-zinc-500">
                  Page {currentPage} of {totalPages.toLocaleString()} — {data.total.toLocaleString()} total
                </span>
                <button
                  onClick={() => setSkip(skip + PAGE_SIZE)}
                  disabled={(skip + PAGE_SIZE) >= data.total}
                  className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-30"
                  data-testid="dir-next"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Browse-by section — gives Google a crawlable index of every
            (sport × country) landing page, multiplying SEO surface ~50×. */}
        <BrowseSection sportSlug={filters.sport} countrySlug={resolvedCountry?.slug} />
      </div>
    </AppLayout>
  );
}

function BrowseSection({ sportSlug, countrySlug }) {
  return (
    <section className="mt-12 pt-8 border-t border-white/5" data-testid="dir-browse">
      <h2 className="font-heading text-xl font-black mb-4">Browse by sport</h2>
      <div className="flex flex-wrap gap-2">
        {SPORTS.filter((s) => s.slug).map((s) => (
          <Link
            key={s.slug}
            to={countrySlug ? `/facilities/sport/${s.slug}/in/${countrySlug}` : `/facilities/sport/${s.slug}`}
            data-testid={`browse-sport-${s.slug}`}
            className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${s.slug === sportSlug ? "bg-[#00E676] text-black border-[#00E676]" : "border-white/10 text-zinc-300 hover:border-[#00E676]/40 hover:text-[#00E676]"}`}
          >{s.name}</Link>
        ))}
      </div>

      <h2 className="font-heading text-xl font-black mb-4 mt-8">Browse by country</h2>
      <div className="flex flex-wrap gap-2">
        {FEATURED_COUNTRIES.map((cs) => {
          const c = COUNTRY_LIST.find((x) => x.slug === cs);
          if (!c) return null;
          const path = sportSlug ? `/facilities/sport/${sportSlug}/in/${c.slug}` : `/facilities/in/${c.slug}`;
          return (
            <Link
              key={c.slug}
              to={path}
              data-testid={`browse-country-${c.slug}`}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${c.slug === countrySlug ? "bg-[#00E676] text-black border-[#00E676]" : "border-white/10 text-zinc-300 hover:border-[#00E676]/40 hover:text-[#00E676]"}`}
            >{c.name}</Link>
          );
        })}
      </div>
    </section>
  );
}

function DirectoryRow({ f }) {
  const accessColor = f.access_type === "public" ? "#00E676" : f.access_type === "private" ? "#FF3B30" : f.access_type === "semi_public" ? "#FFD60A" : null;
  return (
    <Link to={`/facilities/${f.slug || f.id}`}
      data-testid={`dir-row-${f.id}`}
      className="pp-card p-3 block hover:border-[#00E676]/40 transition-colors group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold truncate group-hover:text-[#00E676] transition-colors">{f.name}</span>
            {f.indoor === true && <span className="pp-chip flex items-center gap-0.5"><Building2 size={10} /> Indoor</span>}
            {f.indoor === false && <span className="pp-chip flex items-center gap-0.5"><Trees size={10} /> Outdoor</span>}
            {accessColor && (
              <span
                className="pp-chip"
                style={{ borderColor: `${accessColor}66`, color: accessColor }}
              >
                {f.access_type === "semi_public" ? "Semi-public" : f.access_type === "public" ? "Public" : "Private"}
              </span>
            )}
            {f.verified && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#00E676]/10 text-[#00E676] uppercase tracking-wider">Verified</span>}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <MapPin size={10} />
            {[f.city, f.country].filter(Boolean).join(", ") || "Location unknown"}
            {f.sport_slugs && f.sport_slugs.length > 0 && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="text-zinc-400">{f.sport_slugs.join(" · ")}</span>
              </>
            )}
          </div>
        </div>
        <ExternalLink size={14} className="text-zinc-600 group-hover:text-[#00E676] transition-colors shrink-0 mt-1" />
      </div>
    </Link>
  );
}
